import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  Card,
  Form,
  Grid,
  Header,
  Icon,
  Message,
  Modal,
  Segment,
  Statistic,
} from 'semantic-ui-react';
import { API, showError, showInfo, showSuccess } from '../../helpers';
import { renderQuota } from '../../helpers/render';
import { useTranslation } from 'react-i18next';

const TopUp = () => {
  const { t } = useTranslation();

  const [redemptionCode, setRedemptionCode] = useState('');
  const [userQuota, setUserQuota] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [methods, setMethods] = useState({});
  const [plans, setPlans] = useState([]);
  const [amount, setAmount] = useState('10');
  const [channel, setChannel] = useState('');
  // 立即支付（amount=...）与订阅（plan_id=...）是两个互不相关的支付动作，
  // 各自维护独立的 loading 状态，避免点订阅时“立即支付”按钮也变 loading 给用户造成“同时被点击”的错觉。
  const [paying, setPaying] = useState(false);
  const [subscribingPlanId, setSubscribingPlanId] = useState(0);
  const [subscription, setSubscription] = useState(null);
  const [subscriptionPlanName, setSubscriptionPlanName] = useState('');
  const [trialDaysRemaining, setTrialDaysRemaining] = useState(0);
  const [order, setOrder] = useState(null);
  const pollRef = useRef(null);

  const getUserQuota = async () => {
    const res = await API.get(`/api/user/self`);
    const { success, message, data } = res.data;
    if (success) {
      setUserQuota(data.quota);
      // 免费试用剩余天数（与 EditUser.js 同一公式，基于服务端时间戳）
      const remain = data.free_trial_ends_at
        ? Math.max(
            0,
            Math.ceil((data.free_trial_ends_at - Date.now() / 1000) / 86400)
          )
        : 0;
      setTrialDaysRemaining(remain);
    } else {
      showError(message);
    }
  };

  const loadPaymentMeta = async () => {
    try {
      const [m, p] = await Promise.all([
        API.get('/api/payment/methods'),
        API.get('/api/subscription/plans'),
      ]);
      if (m.data.success) {
        const d = m.data.data || {};
        setMethods(d);
        // 默认选中第一个可用的支付通道（alipay_f2f > xunhupay > mock）
        const first = ['alipay_f2f', 'xunhupay', 'mock'].find(k => d[k]);
        if (first) setChannel(first);
      }
      if (p.data.success) setPlans(p.data.data || []);
    } catch (e) {
      // 静默：支付/订阅元数据失败不应阻塞兑换码流程
    }
  };

  const loadSubscriptionInfo = async () => {
    try {
      const res = await API.get('/api/subscription/me');
      // P4 起 data 形状为 { subscriptions: [{subscription, plan, days_remaining}], ... }，
      // 取剩余天数最多的一条作为「主订阅」沿用原有 UI 展示。
      if (
        res.data.success &&
        res.data.data &&
        Array.isArray(res.data.data.subscriptions) &&
        res.data.data.subscriptions.length > 0
      ) {
        setSubscription(res.data.data.subscriptions[0].subscription);
      } else {
        setSubscription(null);
      }
    } catch (e) {
      // Silent fail -- subscription info is optional display
    }
  };

  // 监听 subscription 和 plans 计算套餐名
  useEffect(() => {
    if (subscription && plans.length > 0) {
      const plan = plans.find(p => p.id === subscription.plan_id);
      setSubscriptionPlanName(plan ? plan.name : `Plan #${subscription.plan_id}`);
    }
  }, [subscription, plans]);

  useEffect(() => {
    getUserQuota();
    loadPaymentMeta();
    loadSubscriptionInfo();
    // 支持 ?order=OUT_TRADE_NO 参数：手机扫码绑定后自动加载订单
    const params = new URLSearchParams(window.location.search);
    const orderId = params.get('order');
    if (orderId) {
      loadOrderFromParam(orderId);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  // loadOrderFromParam 根据 URL 参数自动加载订单
  const loadOrderFromParam = async (outTradeNo) => {
    try {
      const res = await API.get(`/api/payment/order/${outTradeNo}`);
      if (res.data?.success && res.data.data) {
        const o = res.data.data;
        if (o.state === 1) {
          setOrder(o);
          startPolling(o.out_trade_no);
        }
      }
    } catch (e) {
      // 静默
    }
  };

  const topUp = async () => {
    if (redemptionCode === '') {
      showInfo(t('topup.redeem_code.empty_code'));
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await API.post('/api/user/topup', {
        key: redemptionCode,
      });
      const { success, message, data } = res.data;
      if (success) {
        showSuccess(t('topup.redeem_code.success'));
        setUserQuota((quota) => quota + data);
        setRedemptionCode('');
      } else {
        showError(message);
      }
    } catch (err) {
      showError(t('topup.redeem_code.request_failed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const startPolling = (outTradeNo) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await API.get(`/api/payment/order/${outTradeNo}`);
        if (res.data.success && res.data.data) {
          setOrder(prev => prev ? { ...prev, ...res.data.data } : res.data.data);
          if (res.data.data.state === 2) {
            clearInterval(pollRef.current);
            showSuccess(t('topup.alipay.success'));
            getUserQuota();
          } else if (res.data.data.state === 3) {
            clearInterval(pollRef.current);
          }
        }
      } catch (e) {
        // 静默
      }
    }, 2500);
  };

  const submitPayment = async (planId = 0) => {
    // 订阅与立即支付是两条互不耦合的支付路径，各自单独控制 loading：
    //   planId !== 0 → 订阅（用 subscribingPlanId 做 loading）
    //   planId === 0 → 立即支付（用 paying 做 loading）
    const isSubscribe = !!planId;
    if (isSubscribe) {
      setSubscribingPlanId(planId);
    } else {
      setPaying(true);
    }
    try {
      const body = isSubscribe
        ? { plan_id: planId, channel }
        : { amount: parseFloat(amount), channel };
      const res = await API.post('/api/payment/order', body);
      const { success, message, data } = res.data;
      if (!success) {
        showError(message);
        return;
      }
      const initialOrder = { ...data, state: data.state === 'paid' ? 2 : 1 };
      setOrder(initialOrder);
      if (channel === 'mock') {
        showSuccess(t('topup.alipay.mock_success'));
        getUserQuota();
      } else {
        startPolling(data.out_trade_no);
      }
    } catch (e) {
      showError(e.message);
    } finally {
      if (isSubscribe) {
        setSubscribingPlanId(0);
      } else {
        setPaying(false);
      }
    }
  };

  const channelOptions = [];
  if (methods.alipay_f2f) {
    channelOptions.push({
      key: 'a',
      text: t('topup.alipay.channel_alipay_f2f'),
      value: 'alipay_f2f',
    });
  }
  if (methods.xunhupay) {
    channelOptions.push({
      key: 'x',
      text: t('topup.xunhupay.channel_xunhupay'),
      value: 'xunhupay',
    });
  }
  if (methods.mock) {
    channelOptions.push({
      key: 'm',
      text: t('topup.alipay.channel_mock'),
      value: 'mock',
    });
  }

  const stateText = (s) =>
    ({
      1: t('topup.order.state_pending'),
      2: t('topup.order.state_paid'),
      3: t('topup.order.state_closed'),
      4: t('topup.order.state_refunded'),
    }[s] || t('topup.order.state_unknown'));

  const remainingDays = subscription
    ? Math.max(0, Math.ceil((subscription.expires_at - Date.now() / 1000) / 86400))
    : -1;

  return (
    <div className='dashboard-container'>
      <Card fluid className='chart-card'>
        <Card.Content>
          <Card.Header>
            <Header as='h2'>{t('topup.title')}</Header>
          </Card.Header>

          <div style={{ textAlign: 'center', padding: '1em 0' }}>
            <Statistic>
              <Statistic.Value style={{ color: '#2185d0' }}>
                {renderQuota(userQuota, t)}
              </Statistic.Value>
              <Statistic.Label>
                {t('topup.get_code.current_quota')}
              </Statistic.Label>
            </Statistic>
          </div>

          {/* 免费试用剩余天数 */}
          {trialDaysRemaining > 0 && (
            <div style={{ textAlign: 'center', padding: '0 0 1em 0' }}>
              <Message info compact style={{ display: 'inline-block', margin: 0 }}>
                <Icon name='gift' />
                {t('topup.trial.remaining', { days: trialDaysRemaining })}
              </Message>
            </div>
          )}

          {/* 当前订阅信息 */}
          {subscription && subscription.state === 1 && (
            <div style={{ textAlign: 'center', padding: '0 0 1em 0' }}>
              <Statistic size='small'>
                <Statistic.Value style={{ color: remainingDays > 7 ? '#21ba45' : '#f2711c' }}>
                  {remainingDays}
                </Statistic.Value>
                <Statistic.Label>
                  {t('topup.subscription.remaining_days', { days: remainingDays })}
                </Statistic.Label>
              </Statistic>
              {subscriptionPlanName && (
                <div style={{ color: '#666', fontSize: '0.9em', marginTop: '0.3em' }}>
                  {t('topup.subscription.plan')}: {subscriptionPlanName}
                  &nbsp;|&nbsp;
                  {t('topup.subscription.expires_at')}: {new Date(subscription.expires_at * 1000).toLocaleDateString()}
                </div>
              )}
            </div>
          )}
          {!subscription && (
            <div style={{ textAlign: 'center', padding: '0 0 0.5em 0', color: '#999', fontSize: '0.85em' }}>
              <Icon name='info circle' />
              &nbsp;{t('topup.subscription.no_active')}
            </div>
          )}

          <Grid columns={2} stackable>
            <Grid.Column>
              <Card
                fluid
                style={{
                  height: '100%',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                }}
              >
                <Card.Content
                  style={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Card.Header>
                    <Header as='h3' style={{ color: '#21ba45', margin: '1em' }}>
                      <i className='ticket alternate icon'></i>
                      {t('topup.redeem_code.title')}
                    </Header>
                  </Card.Header>
                  <Card.Description
                    style={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <div
                      style={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Form.Input
                        fluid
                        icon='key'
                        iconPosition='left'
                        placeholder={t('topup.redeem_code.placeholder')}
                        value={redemptionCode}
                        onChange={(e) => {
                          setRedemptionCode(e.target.value);
                        }}
                        onPaste={(e) => {
                          e.preventDefault();
                          const pastedText = e.clipboardData.getData('text');
                          setRedemptionCode(pastedText.trim());
                        }}
                        action={
                          <Button
                            icon='paste'
                            content={t('topup.redeem_code.paste')}
                            onClick={async () => {
                              try {
                                const text =
                                  await navigator.clipboard.readText();
                                setRedemptionCode(text.trim());
                              } catch (err) {
                                showError(t('topup.redeem_code.paste_error'));
                              }
                            }}
                          />
                        }
                      />

                      <div style={{ paddingBottom: '1em' }}>
                        <Button
                          color='green'
                          fluid
                          size='large'
                          onClick={topUp}
                          loading={isSubmitting}
                          disabled={isSubmitting}
                        >
                          {isSubmitting
                            ? t('topup.redeem_code.submitting')
                            : t('topup.redeem_code.submit')}
                        </Button>
                      </div>
                    </div>
                  </Card.Description>
                </Card.Content>
              </Card>
            </Grid.Column>

            <Grid.Column>
              <Card
                fluid
                style={{
                  height: '100%',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                }}
              >
                <Card.Content
                  style={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Card.Header>
                    <Header as='h3' style={{ color: '#2185d0', margin: '1em' }}>
                      <i className='money bill alternate icon'></i>
                      {t('topup.online_pay.title')}
                    </Header>
                  </Card.Header>
                  <Card.Description style={{ flex: 1 }}>
                    {channelOptions.length === 0 ? (
                      <Message warning>
                        <Message.Header>
                          {t('topup.alipay.no_method')}
                        </Message.Header>
                        <p>{t('topup.alipay.no_method_hint')}</p>
                      </Message>
                    ) : (
                      <Form>
                        <Form.Input
                          label={t('topup.alipay.amount_label')}
                          type='number'
                          step='0.01'
                          min='0.01'
                          value={amount}
                          onChange={(e) => setAmount(e.target.value)}
                        />

                        {channelOptions.length > 1 && (
                          <Form.Select
                            label={t('topup.alipay.channel_label')}
                            options={channelOptions}
                            value={channel}
                            onChange={(_, { value }) => setChannel(value)}
                          />
                        )}

                        <Button
                          primary
                          fluid
                          size='large'
                          loading={paying}
                          disabled={paying || subscribingPlanId !== 0 || !channel}
                          onClick={() => submitPayment(0)}
                        >
                          {t('topup.online_pay.submit')}
                        </Button>
                      </Form>
                    )}
                  </Card.Description>
                </Card.Content>
              </Card>
            </Grid.Column>
          </Grid>

          {plans.length > 0 && (
            <Segment style={{ marginTop: '1.5em' }}>
              <Header as='h3'>
                <i className='cubes icon'></i>
                {t('topup.subscription.title')}
              </Header>
              <Grid columns={Math.min(plans.length, 4)} stackable doubling>
                {plans.map((p) => (
                  <Grid.Column key={p.id}>
                    <Segment>
                      <Header as='h4' style={{ marginBottom: 4 }}>
                        {p.name}
                      </Header>
                      <div style={{ color: '#888', fontSize: 12 }}>
                        {p.code}
                      </div>
                      <div style={{ margin: '0.6em 0', fontSize: 18 }}>
                        ¥{(p.price_cents / 100).toFixed(2)}
                        <span style={{ color: '#888', fontSize: 12 }}>
                          {' '}
                          / {p.period_days}
                          {t('topup.subscription.period_days')}
                        </span>
                      </div>
                      {p.description && (
                        <p style={{ color: '#666', minHeight: '3em' }}>
                          {p.description}
                        </p>
                      )}
                      {p.price_cents > 0 ? (
                        <Button
                          primary
                          fluid
                          loading={subscribingPlanId === p.id}
                          disabled={
                            paying ||
                            subscribingPlanId !== 0 ||
                            !channel ||
                            channelOptions.length === 0
                          }
                          onClick={() => submitPayment(p.id)}
                        >
                          {t('topup.subscription.subscribe')}
                        </Button>
                      ) : (
                        <Button basic fluid disabled>
                          {t('topup.subscription.free')}
                        </Button>
                      )}
                    </Segment>
                  </Grid.Column>
                ))}
              </Grid>
            </Segment>
          )}

          {/* 支付二维码弹窗 */}
          <Modal
            open={!!order && order.state === 1}
            size='small'
            centered
            onClose={() => {
              if (pollRef.current) clearInterval(pollRef.current);
              setOrder(null);
            }}
            closeOnDimmerClick={false}
          >
            <Modal.Header>
              {t('topup.order.title')} {order?.out_trade_no}
            </Modal.Header>
            <Modal.Content style={{ textAlign: 'center' }}>
              <p>
                {t('topup.order.state')}:{' '}
                <strong>{stateText(order?.state)}</strong>
              </p>
              {order?.qr_code && (
                <>
                  <p>{t('topup.alipay.scan_hint')}</p>
                  <img
                    alt='qr'
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=280x280&data=${encodeURIComponent(
                      order.qr_code
                    )}`}
                    style={{ margin: '1em 0' }}
                  />
                  <p
                    style={{
                      wordBreak: 'break-all',
                      color: '#999',
                      fontSize: 11,
                    }}
                  >
                    {t('topup.order.qr_raw')}
                    {order.qr_code}
                  </p>
                </>
              )}
            </Modal.Content>
            <Modal.Actions>
              <Button
                onClick={() => {
                  if (pollRef.current) clearInterval(pollRef.current);
                  setOrder(null);
                }}
              >
                {t('topup.order.close')}
              </Button>
            </Modal.Actions>
          </Modal>

          {/* 支付成功弹窗 */}
          <Modal
            open={!!order && order.state === 2}
            size='tiny'
            centered
            onClose={() => setOrder(null)}
          >
            <Modal.Content style={{ textAlign: 'center', padding: '2.5em' }}>
              <Icon
                name='check circle'
                color='green'
                size='huge'
                style={{ marginBottom: '0.5em' }}
              />
              <Header as='h2' style={{ color: '#21ba45' }}>
                {t('topup.order.pay_success')}
              </Header>
              <p style={{ color: '#666' }}>
                {t('topup.order.title')} {order?.out_trade_no}
              </p>
            </Modal.Content>
            <Modal.Actions style={{ textAlign: 'center' }}>
              <Button
                positive
                onClick={() => setOrder(null)}
              >
                {t('topup.order.confirm')}
              </Button>
            </Modal.Actions>
          </Modal>
        </Card.Content>
      </Card>
    </div>
  );
};

export default TopUp;
